import urlJoin from 'url-join';
import { useRuntimeConfig } from '~/composables/useRuntimeConfig';
import { useStore } from '~/composables/useStore';
import { url, nonNullable } from '~/utils';
import { ECSchema } from '~/types/api';
import { OrderSimulations } from '~/types/modules/order';

export const useKarteItemsInfo = () => {
  const store = useStore();
  const { FRONTEND_ORIGIN } = useRuntimeConfig();

  const filterItems = (items: any[]) => {
    const filteredItems = items.filter(nonNullable);
    return filteredItems.length > 0 ? filteredItems : undefined;
  };

  const getParsedItems = (items: any): any[] => {
    return items.map((item: any) => ({
      item_id: item?.product_code_s,
      sku_id: '',
      name: item?.product_name_s,
      price: item?.lowest_price_general_i,
      item_url: item?.product_code_s
        ? urlJoin(
            FRONTEND_ORIGIN,
            url(item?.product_type_s === '頒布会' ? 'ITEM_DISTRIBUTION_DETAIL' : 'ITEM_DETAIL', {
              code: item.product_code_s,
            })
          )
        : undefined,
      image_url: item?.main_image_url_s,
      item_type: item?.product_type_s,
      parent_category_id: item?.category_id_i,
      parent_category_name: item?.category_s,
      category_id: item?.category_id_name_s?.split('_')[1],
      category_name: item?.category_id_name_s?.split('_')[2],
      area1_id: item?.area_id_1_i,
      area1_name: item?.area_1_s,
      area2_id: item?.area_id_2_i,
      area2_name: item?.area_2_s,
      area3_id: item?.area_id_3_i,
      area3_name: item?.area_3_s,
      producer_id: item?.producer_id_i,
      producer_name: item?.producer_s,
      base_item_code: '',
      item_size: item?.size_amount_i || undefined,
      color_group_id: item?.parent_color_id_i,
      color_group_name: item?.parent_color_s,
      color_id: item?.color_id_i,
      color_name: item.color_group_s,
      brand1_id: Number(item?.facet_brand_id_text_sm?.[0]?.split('_')[1]) || undefined,
      brand2_id: Number(item?.facet_brand_id_text_sm?.[1]?.split('_')[1]) || undefined,
      brand3_id: Number(item?.facet_brand_id_text_sm?.[2]?.split('_')[1]) || undefined,
      taste_gauge: item?.taste_gauge_i,
      sense_type: !isNaN(parseInt(item?.sense_type_s, 10)) ? parseInt(item?.sense_type_s, 10) : undefined,
      taste1: Number(item?.taste_finish_s) || undefined,
      taste2: Number(item?.taste_volume_s) || undefined,
      taste3: Number(item?.taste_fruity_s) || undefined,
      taste4: Number(item?.taste_bitter_s) || undefined,
      taste5: Number(item?.taste_robust_s) || undefined,
      taste6: Number(item?.taste_sour_s) || undefined,
      review_point: item?.sort_r_rating_r,
      carriage_discount_type: item?.shipping_fee_discount_flag_i ? '送料無料' : '',
      list_display_flg: item?.display_flag_i ? '表示' : '非表示',
    }));
  };

  const getCartItemsInfo = async (
    simulatedData: Array<OrderSimulations>,
    cartId?: number,
    rawCarts: ECSchema<'cart.Cart'>[] = store.state.modules.cart.carts
  ) => {
    const carts = cartId ? rawCarts.filter((cart) => cart.cart_id === cartId) : rawCarts;

    const idsList = carts.flatMap(({ items }) => items.map((item) => `product_code_s:${item.product_code}`));
    const { distributionCourseId } = store.state.modules.order.userOption;
    if (distributionCourseId) {
      idsList.push(`iid:distribution_${distributionCourseId}`);
    }
    let items = [];
    try {
      const res = await store.dispatch('modules/product/fetchProductsVilla', { q: idsList.join(' OR ') });
      items = res.items;
    } catch {
      return null;
    }

    const cartItems = cartId
      ? rawCarts.find((cart) => cart.cart_id === cartId)?.items || []
      : rawCarts.flatMap((cart) => cart.items);
    const parsedItems = getParsedItems(items);
    const parsedCartItems = cartItems.map((cartItem) => {
      const item = parsedItems.find((parsedItem: any) => String(parsedItem.item_id) === cartItem.product_code);
      let simulatedPrice = 0;
      simulatedData.forEach((data) => {
        const discountInfo = data.item_discounts.find(
          (item) => item.product_variant_id === Number(cartItem.product_variant_id)
        );
        if (!discountInfo) {
          return;
        }
        simulatedPrice = discountInfo.applied_discount_price_including_tax;
      });
      return {
        ...item,
        sku_id: cartItem.prduct_enterprise_system_code,
        base_item_code: cartItem.prduct_enterprise_system_code,
        price: simulatedPrice || cartItem.price,
        quantity: cartItem.quantity,
      };
    });

    if (distributionCourseId) {
      const distributionItemInfo = {
        ...parsedItems.find((item) => item.item_type === '頒布会'),
        quantity: 1,
      };
      parsedCartItems.push(distributionItemInfo);
    }

    return {
      items: parsedCartItems,
      item_item_ids: filterItems(parsedCartItems.map((item: any) => item.item_id)),
      item_sku_ids: filterItems(parsedCartItems.map((item: any) => item.sku_id)),
      item_names: filterItems(parsedCartItems.map((item: any) => item.name)),
      item_prices: filterItems(parsedCartItems.map((item: any) => item.price)),
      item_quantities: filterItems(parsedCartItems.map((item: any) => item.quantity)),
      item_item_urls: filterItems(parsedCartItems.map((item: any) => item.item_url)),
      item_image_urls: filterItems(parsedCartItems.map((item: any) => item.image_url)),
      item_item_types: filterItems(parsedCartItems.map((item: any) => item.item_type)),
      item_parent_category_ids: filterItems(parsedCartItems.map((item: any) => item.parent_category_id)),
      item_parent_category_names: filterItems(parsedCartItems.map((item: any) => item.parent_category_name)),
      item_category_ids: filterItems(parsedCartItems.map((item: any) => item.category_id)),
      item_category_names: filterItems(parsedCartItems.map((item: any) => item.category_name)),
      item_area1_ids: filterItems(parsedCartItems.map((item: any) => item.area1_id)),
      item_area1_names: filterItems(parsedCartItems.map((item: any) => item.area1_name)),
      item_area2_ids: filterItems(parsedCartItems.map((item: any) => item.area2_id)),
      item_area2_names: filterItems(parsedCartItems.map((item: any) => item.area2_name)),
      item_area3_ids: filterItems(parsedCartItems.map((item: any) => item.area3_id)),
      item_area3_names: filterItems(parsedCartItems.map((item: any) => item.area3_name)),
      item_producer_ids: filterItems(parsedCartItems.map((item: any) => item.producer_id)),
      item_producer_names: filterItems(parsedCartItems.map((item: any) => item.producer_name)),
      item_base_item_codes: filterItems(parsedCartItems.map((item: any) => item.base_item_code)),
      item_item_sizes: filterItems(parsedCartItems.map((item: any) => item.item_size)),
      item_color_group_ids: filterItems(parsedCartItems.map((item: any) => item.color_group_id)),
      item_color_group_names: filterItems(parsedCartItems.map((item: any) => item.color_group_name)),
      item_color_ids: filterItems(parsedCartItems.map((item: any) => item.color_id)),
      item_color_names: filterItems(parsedCartItems.map((item: any) => item.color_name)),
      item_brand1_ids: filterItems(parsedCartItems.map((item: any) => item.brand1_id)),
      item_brand2_ids: filterItems(parsedCartItems.map((item: any) => item.brand2_id)),
      item_brand3_ids: filterItems(parsedCartItems.map((item: any) => item.brand3_id)),
      item_taste_gauges: filterItems(parsedCartItems.map((item: any) => item.taste_gauge)),
      item_sense_types: filterItems(parsedCartItems.map((item: any) => item.sense_type)),
      item_taste1s: filterItems(parsedCartItems.map((item: any) => item.taste1)),
      item_taste2s: filterItems(parsedCartItems.map((item: any) => item.taste2)),
      item_taste3s: filterItems(parsedCartItems.map((item: any) => item.taste3)),
      item_taste4s: filterItems(parsedCartItems.map((item: any) => item.taste4)),
      item_taste5s: filterItems(parsedCartItems.map((item: any) => item.taste5)),
      item_taste6s: filterItems(parsedCartItems.map((item: any) => item.taste6)),
      item_review_points: filterItems(parsedCartItems.map((item: any) => item.review_point)),
      item_carriage_discount_types: filterItems(parsedCartItems.map((item: any) => item.carriage_discount_type)),
      item_list_display_flgs: filterItems(parsedCartItems.map((item: any) => item.list_display_flg)),
    };
  };

  return { getParsedItems, getCartItemsInfo };
};
